import React, { useState } from 'react';
import { format, subDays, startOfMonth, endOfMonth, startOfWeek } from 'date-fns';
import { Calendar, ChevronDown } from 'lucide-react';

export default function DateRangeSelector({ 
  dateRange, 
  comparisonRange, 
  compareMode, 
  onDateRangeChange, 
  onComparisonRangeChange,
  onCompareModeToggle 
}) {
  const [showPresets, setShowPresets] = useState(false);

  const presets = [
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'Last 90 Days', days: 90 },
    { label: 'This Month', custom: 'thisMonth' },
    { label: 'Last Month', custom: 'lastMonth' },
    { label: 'This Week', custom: 'thisWeek' }
  ];

  const handlePresetSelect = (preset) => {
    const endDate = new Date();
    let startDate;
    let compareStart = null;
    let compareEnd = null;

    if (preset?.custom === 'thisMonth') {
      startDate = startOfMonth(endDate);
    } else if (preset?.custom === 'lastMonth') {
      const lastMonth = subDays(startOfMonth(endDate), 1);
      startDate = startOfMonth(lastMonth);
      endDate?.setTime(endOfMonth(lastMonth)?.getTime());
    } else if (preset?.custom === 'thisWeek') {
      startDate = startOfWeek(endDate);
    } else {
      startDate = subDays(endDate, preset?.days);
      
      if (compareMode) {
        // Auto-generate comparison range (previous period)
        compareEnd = subDays(startDate, 1);
        compareStart = subDays(compareEnd, preset?.days);
      }
    }

    onDateRangeChange({ startDate, endDate });
    
    if (compareMode && compareStart && compareEnd) {
      onComparisonRangeChange({ startDate: compareStart, endDate: compareEnd });
    }
    
    setShowPresets(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Date Range Selection</h3>
        <button
          onClick={() => onCompareModeToggle(!compareMode)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            compareMode 
              ? 'bg-blue-600 text-white hover:bg-blue-700' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {compareMode ? 'Comparison Enabled' : 'Enable Comparison'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Primary Date Range */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Primary Period
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <input
                type="date"
                value={dateRange?.startDate ? format(dateRange?.startDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => onDateRangeChange({
                  ...dateRange,
                  startDate: new Date(e?.target?.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <span className="text-gray-500">to</span>
            <div className="flex-1">
              <input
                type="date"
                value={dateRange?.endDate ? format(dateRange?.endDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => onDateRangeChange({
                  ...dateRange,
                  endDate: new Date(e?.target?.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Comparison Date Range */}
        {compareMode && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Comparison Period
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <input
                  type="date"
                  value={comparisonRange?.startDate ? format(comparisonRange?.startDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => onComparisonRangeChange({
                    ...comparisonRange,
                    startDate: new Date(e?.target?.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <span className="text-gray-500">to</span>
              <div className="flex-1">
                <input
                  type="date"
                  value={comparisonRange?.endDate ? format(comparisonRange?.endDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => onComparisonRangeChange({
                    ...comparisonRange,
                    endDate: new Date(e?.target?.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Presets */}
      <div className="mt-4 relative">
        <button
          onClick={() => setShowPresets(!showPresets)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <Calendar size={16} />
          <span className="text-sm font-medium">Quick Presets</span>
          <ChevronDown size={16} className={`transition-transform ${showPresets ? 'rotate-180' : ''}`} />
        </button>

        {showPresets && (
          <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[200px]">
            {presets?.map((preset, index) => (
              <button
                key={index}
                onClick={() => handlePresetSelect(preset)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg transition-colors"
              >
                <span className="text-sm text-gray-700">{preset?.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}